import type { MutationResult } from '@apollo/client';
import { useCallback } from 'react';

import { removeScanFromProjectUpdater } from 'src/shared/graphql/cache/Project';
import type {
  BasicJobInfoFragment,
  RemoveScanFromProjectMutation,
} from 'src/shared/graphql/generated';
import { useRemoveScanFromProjectMutation } from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

type DeleteHandlerParams = {
  onSuccess: () => void;
  onError: () => void;
};

export const useRemoveScanFromProjectHandler = (
  projectId: number,
  scan?: BasicJobInfoFragment,
): [
  (params: DeleteHandlerParams) => void,
  MutationResult<RemoveScanFromProjectMutation>,
] => {
  const [removeScanFromProjectMutation, removeScanFromProjectResponse] =
    useRemoveScanFromProjectMutation();

  const deleteProjectClickHandler = useCallback(
    async ({ onSuccess, onError }: DeleteHandlerParams) => {
      if (!projectId || !scan) return;

      const optimisticResponse = {
        deleteProjectAsset: {
          projectId,
          addableId: scan.id,
          addableType: 'JOB',
        },
      };

      const removeScanFromProjectResult = await removeScanFromProjectMutation({
        variables: {
          projectId,
          scanId: scan.id,
        },
        update: removeScanFromProjectUpdater({
          projectIdToUpdate: projectId,
          scan,
        }),
        optimisticResponse,
      });

      if (removeScanFromProjectResult.errors?.length) {
        removeScanFromProjectResult.errors.forEach(e => logError(e));
        onError();
        return;
      }
      onSuccess();
    },
    [projectId, removeScanFromProjectMutation, scan],
  );

  return [deleteProjectClickHandler, removeScanFromProjectResponse];
};
